// CIP fork: one PostHog identity across Studio, the Auth0 login Action and
// the data platform, and the rules for the anonymous mode public pages run in
// (README.md in this folder). Pure, so client and server share it.
import { maskSecretPath } from "@langfuse/shared";

/** Prefix for users who never signed in through Auth0 (e.g. local dev). */
export const STUDIO_DISTINCT_ID_PREFIX = "studio:";

/**
 * The PostHog `distinct_id` for a Studio user: their Auth0 user id (`sub`),
 * which the Auth0 Action also sends, so a flag targeted at a person resolves
 * the same way at login and in the app. Never an email.
 */
export const analyticsIdFor = ({
  userId,
  auth0Sub,
}: {
  userId: string;
  auth0Sub: string | null | undefined;
}): string => auth0Sub || `${STUDIO_DISTINCT_ID_PREFIX}${userId}`;

// Pages respondents, partners and signed-out visitors see. PostHog runs on
// them in anonymous mode: pageview and pageleave only, nothing written to
// the browser, no identity, URLs stripped of anything that could name a
// person or unlock a page (capability tokens: `SECRET_PATH_ROUTES` in
// @langfuse/shared, which Sentry masks too). Never gated by a page-area flag.
// `/f/` is the short respondent link (see FORK.md): it only ever redirects to
// `/public/forms/`, so it is public for the same reason and never gated.
const PUBLIC_ROUTE_PREFIXES = [
  "/public/",
  "/partner/",
  "/auth/",
  "/f/",
] as const;

/** `pathname` is the Next.js route pattern (`router.pathname`). */
export const isPublicAnalyticsRoute = (pathname: string): boolean =>
  PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * A URL reduced to origin and masked path: no query string (partner platform
 * ids arrive there), no fragment (the participant id travels there). A value
 * that is not an absolute URL is dropped rather than guessed at.
 */
export const sanitizePublicUrl = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value === "") return undefined;
  try {
    const url = new URL(value);
    return `${url.origin}${maskSecretPath(url.pathname)}`;
  } catch {
    return undefined;
  }
};

/** The only events an anonymous page may send. */
export const PUBLIC_EVENTS = new Set(["$pageview", "$pageleave"]);

const URL_PROPERTY = /url|referrer/i;
const PATHNAME_PROPERTY = /pathname/i;

type CaptureLike = {
  event: string;
  properties?: Record<string, unknown>;
  $set?: unknown;
  $set_once?: unknown;
};

/**
 * `before_send` for anonymous mode: only pageview and pageleave pass, every
 * URL-ish property is reduced to origin and masked path, every pathname is
 * masked, and nothing is written to the person (there is none).
 */
export const sanitizePublicEvent = <T extends CaptureLike>(
  event: T | null,
): T | null => {
  if (!event || !PUBLIC_EVENTS.has(event.event)) return null;
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event.properties ?? {})) {
    if (key === "$set" || key === "$set_once") continue;
    if (URL_PROPERTY.test(key)) {
      const clean = sanitizePublicUrl(value);
      if (clean !== undefined) properties[key] = clean;
    } else if (PATHNAME_PROPERTY.test(key) && typeof value === "string") {
      properties[key] = maskSecretPath(value);
    } else {
      properties[key] = value;
    }
  }
  const { $set: _set, $set_once: _setOnce, ...rest } = event;
  return { ...rest, properties } as T;
};

/**
 * Where people open PostHog, from the ingestion host Studio is configured
 * with (`POSTHOG_HOST`): `us.i.posthog.com` → `us.posthog.com`, likewise for
 * EU. A self-hosted or proxied host is used as is.
 */
export const posthogUiHost = (ingestHost: string): string =>
  ingestHost.replace(
    /^(https?:\/\/)(us|eu)\.i\.posthog\.com\/?$/,
    "$1$2.posthog.com",
  );

/** PostHog's flag list filtered to one key (it opens the current project). */
export const posthogFlagUrl = (ingestHost: string, flagKey: string): string =>
  `${posthogUiHost(ingestHost)}/feature_flags?search=${encodeURIComponent(flagKey)}`;

/**
 * Whether an email belongs to the team, by domain
 * (`STUDIO_INTERNAL_EMAIL_DOMAINS`, comma-separated). The boolean is the only
 * thing that reaches PostHog, as the `internal` person property: the email
 * never does. Nobody is internal while the variable is unset.
 */
export const isInternalEmail = (
  email: string | null | undefined,
  domainsCsv: string | undefined,
): boolean => {
  if (!email || !domainsCsv) return false;
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domainsCsv
    .split(",")
    .map((d) => d.trim().toLowerCase().replace(/^@/, ""))
    .filter(Boolean)
    .some((d) => domain === d);
};
